import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Button, ErrorText, Flex, FormField, Modal, Text, TextField } from '@cmaas/ui';
import type { AdminPortalUser } from '../../api/types';
import { apiFetch, CmaasApiError } from '../../api/client';

const createSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(255),
  password: z.string().min(12),
});
type CreateValues = z.infer<typeof createSchema>;

const mutationError = (err: unknown) => (err instanceof CmaasApiError ? err.message : 'Something went wrong.');

export function NewPortalUserModal({
  tenantId,
  open,
  onOpenChange,
}: {
  tenantId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const key = ['tenant-portal-users', tenantId];

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateValues>({ resolver: zodResolver(createSchema) });

  const create = useMutation({
    mutationFn: (values: CreateValues) =>
      apiFetch<AdminPortalUser>(`/v1/admin/tenants/${tenantId}/portal-users`, { method: 'POST', body: values }),
    onSuccess: () => {
      reset();
      void queryClient.invalidateQueries({ queryKey: key });
      onOpenChange(false);
    },
  });

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      reset();
      create.reset();
    }
    onOpenChange(next);
  };

  return (
    <Modal open={open} onOpenChange={handleOpenChange} title="New portal user">
      <form onSubmit={handleSubmit((v) => create.mutate(v))}>
        <Flex direction="column" gap="3" mt="4">
          <Text as="p" size="2" color="gray">
            Creates the tenant's first portal user only — invite additional teammates or change roles from the
            tenant's own Team screen.
          </Text>
          <FormField label="Name" error={errors.name?.message}>
            <TextField.Root {...register('name')} />
          </FormField>
          <FormField label="Email" error={errors.email?.message}>
            <TextField.Root type="email" {...register('email')} />
          </FormField>
          <FormField label="Password" error={errors.password?.message}>
            <TextField.Root type="password" {...register('password')} />
          </FormField>
          {create.isError && <ErrorText>{mutationError(create.error)}</ErrorText>}
          <Flex gap="3" justify="end" mt="2">
            <Button type="button" variant="soft" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              Create portal user
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
}
